import { action, computed, observable } from 'mobx';
import { ApiUrls } from '@app/AppConstants';
import ApiService from '@app/Services/ApiService';
import { navigationStore } from '@app/Stores/NavigationStore';
import { NotificationHandler } from '@app/Components/ToastNotification';
import {CountryModel, PersonInfoModel} from '@app/Models';
import { CompletionType, PromiseCompletion } from '@app/Classes/PromiseCompletion';

export type ValidateFormCallback = (field: string, isValid: boolean) => void;
export type ChangeCheckboxCallback = (field: keyof PersonInfoModel, value: boolean) => void;
export type ChangeDateFieldCallback = (field: keyof PersonInfoModel, value: Date | undefined) => void;
export type ChangeFieldCallback = (field: keyof PersonInfoModel, value: Date | string | boolean | number | undefined ) => void;

export default class PersonInfoStore {
    @observable personInfo: PersonInfoModel;
    @observable nationalities: CountryModel[];
    @observable dataLoading: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    @observable dataChanged: boolean = false;
    @observable isFormValid: boolean = true;
    @observable invalidFields: Set<string> = new Set();
    private _personInfoOrigin: PersonInfoModel;
    private _personId: string;
    
    constructor(id: string){
        this.loadPersonInfo(id);
    }

    @action.bound
    async loadPersonInfo(personId: string) {
        this._personId = personId;

        try{
            const params: IPersonInfoParam = { id: personId };
            const { data: person } = await ApiService.getTypedData<PersonInfoModel>(ApiUrls.PersonInfoUrl, params, { completion: this.dataLoading });
            this.invalidFields = new Set<string>();
            this._personInfoOrigin = { ...person };
            this.personInfo = person;
            this.validateForm();
        } catch (e){
            if ((e as { status: number }).status === 403) {
                return navigationStore.redirect('/403');
            }
        }

        await this.loadNationalities();
    }

    @action.bound
    async loadNationalities() {
        const { data: nationalities } = await ApiService.getTypedData<CountryModel[]>(ApiUrls.PersonNationalitiestUrl, null, { completion: this.dataLoading });
        this.nationalities = nationalities;
    }

    @action.bound
    async save() {
        if (JSON.stringify(this.personInfo) === JSON.stringify(this._personInfoOrigin)) return;
        
        try{
            await ApiService.putTypedData<PersonInfoModel>(ApiUrls.PersonInfoUrl, this.personInfo);
            this._personInfoOrigin = { ...this.personInfo };
                this.dataChanged = false;
                NotificationHandler.showSuccess('Person Information was updated', 'Success message');
        } catch (e){
            if ((e as { status: number }).status === 403) {
                return navigationStore.redirect('/403');
            }
        }
    }

    @action.bound
    cancel() {
        this.personInfo = { ...this._personInfoOrigin };
        this.validateForm();
    }

    @action.bound
    validateForm(field?: string, isDelete?: boolean) {
        if (!field) {
            this.invalidFields = new Set();
        } else {
            if (isDelete) this.invalidFields.delete(field);
            else this.invalidFields.add(field);
        }
        this.isFormValid = this.invalidFields.size === 0;
    }

    @action.bound
    isValidField(field: string) {
        //const result = this.invalidFields && (this.invalidFields.size === 0 || !this.invalidFields.has(field));
        return computed(() => !this.invalidFields || this.invalidFields && (this.invalidFields.size === 0 || !this.invalidFields.has(field))).get();
    }

    @computed
    get isEnabled(): boolean {
        const enabled = JSON.stringify(this.personInfo) !== JSON.stringify(this._personInfoOrigin);
        if (enabled && !this.dataChanged) this.dataChanged = true;
        return enabled && this.dataChanged;
    }

    get personGuid(){
        return this._personId;
    }

    @action.bound
    uploadPersonImage(personId: string) {
        if (personId !== this.personInfo.id)
            return;
        this.personInfo.personImages = [];
        this._personInfoOrigin.personImages = [];
    }

    @action.bound
    onChangeCheckboxCallback(field: keyof PersonInfoModel, value: boolean) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.personInfo as any)[field] = value;
    }

    @action.bound
    onChangeDateFieldCallback(field: keyof PersonInfoModel, value: Date | undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.personInfo as any)[field] = value;
    }    
    
    @action.bound
    onChangeTextFieldCallback(field: keyof PersonInfoModel, value: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.personInfo as any)[field] = value;
    }
    
    @action.bound
    onChangeFieldCallback(field: keyof PersonInfoModel, value: Date | string | boolean | number | undefined ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this.personInfo as any)[field] = value;
    }
}

interface IPersonInfoParam {
    id: string;
}